/**
 * Helpers for FilterDepartmentsModal apply payload <-> chips / applied-filter array /
 * Department list API query params (ER-10685).
 *
 * Payload values are stable snake_case; labels are for display only:
 *   - statuses:  { draft, active, retired } (checkbox booleans)
 *   - archived:  do_not_show_archived (default) | show_archived
 *   - manager:   no_filter_manager (default) | has_manager | no_manager
 *   - positions: no_filter_positions (default) | has_positions | no_positions
 *
 * The persisted applied-filter array (organizationDepartmentsData) holds status slugs,
 * `show_archived`, and the Manager / Positions LABELS. Defaults are never persisted, so the
 * header-dashboard tile matching and the "non-default filters" check keep working.
 */

export const STATUS_LABEL_BY_VALUE = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
};

export const ARCHIVED_LABEL_BY_VALUE = {
  do_not_show_archived: "Do Not Show",
  show_archived: "Show",
};

export const MANAGER_LABEL_BY_VALUE = {
  has_manager: "Has Manager",
  no_manager: "Does Not Have Manager",
};

export const POSITIONS_LABEL_BY_VALUE = {
  has_positions: "Has Positions",
  no_positions: "Does Not Have Positions",
};

export const SHOW_ARCHIVED_CHIP = "Show Archived";

export const DEFAULT_DEPARTMENT_FILTER_PAYLOAD = Object.freeze({
  statuses: Object.freeze({ draft: true, active: true, retired: false }),
  archived: "do_not_show_archived",
  manager: "no_filter_manager",
  positions: "no_filter_positions",
});

const RADIO_LABELS = {
  archived: ARCHIVED_LABEL_BY_VALUE,
  manager: MANAGER_LABEL_BY_VALUE,
  positions: POSITIONS_LABEL_BY_VALUE,
};

const RADIO_KEYS = Object.keys(RADIO_LABELS);

// Slugs written by the SearchModal-based filter before this modal existed.
const LEGACY_ALIASES = {
  archived: ["archived", "show_archived"],
  no_archived: ["archived", "do_not_show_archived"],
};

const RADIO_LOOKUP = (() => {
  const lookup = { ...LEGACY_ALIASES };
  RADIO_KEYS.forEach((radioKey) => {
    Object.entries(RADIO_LABELS[radioKey]).forEach(([value, label]) => {
      lookup[value] = [radioKey, value];
      lookup[label.toLowerCase()] = [radioKey, value];
    });
  });
  return lookup;
})();

function toFilterString(filter) {
  const raw = filter !== null && typeof filter === "object" ? filter.value : filter;
  return String(raw ?? "").trim();
}

export function cloneDepartmentFilterPayload(payload = DEFAULT_DEPARTMENT_FILTER_PAYLOAD) {
  const next = {
    statuses: {
      draft: !!payload?.statuses?.draft,
      active: !!payload?.statuses?.active,
      retired: !!payload?.statuses?.retired,
    },
  };
  RADIO_KEYS.forEach((radioKey) => {
    next[radioKey] = payload?.[radioKey] || DEFAULT_DEPARTMENT_FILTER_PAYLOAD[radioKey];
  });
  return next;
}

/** Payload -> persisted applied-filter array; default radio values are omitted. */
export function payloadToAppliedFilters(payload = {}) {
  const filters = [];

  Object.entries(payload.statuses || {}).forEach(([key, checked]) => {
    if (checked && STATUS_LABEL_BY_VALUE[key]) filters.push(key);
  });

  if (payload.archived === "show_archived") filters.push("show_archived");

  const manager = MANAGER_LABEL_BY_VALUE[payload.manager];
  if (manager) filters.push(manager);

  const positions = POSITIONS_LABEL_BY_VALUE[payload.positions];
  if (positions) filters.push(positions);

  return filters;
}

/** Applied-filter array (slugs, labels or `{ value }` objects) -> modal payload. */
export function appliedFiltersToPayload(filters = []) {
  const entries = Array.isArray(filters) ? filters : [];
  const payload = cloneDepartmentFilterPayload({
    statuses: { draft: false, active: false, retired: false },
  });

  entries.forEach((entry) => {
    const key = toFilterString(entry).toLowerCase();
    if (!key) return;

    if (STATUS_LABEL_BY_VALUE[key]) {
      payload.statuses[key] = true;
      return;
    }

    const match = RADIO_LOOKUP[key];
    if (match) payload[match[0]] = match[1];
  });

  return payload;
}

/**
 * Applied-filter array -> Departments list API filters. Archived departments are
 * excluded unless "Show" is selected: the status list is the checked statuses (all
 * non-archived statuses when none is checked), plus `archived` when Show is selected.
 */
export function convertDepartmentFiltersToApiFormat(filters = []) {
  const payload = appliedFiltersToPayload(filters);
  const checked = Object.keys(STATUS_LABEL_BY_VALUE).filter((key) => payload.statuses[key]);
  const status = checked.length > 0 ? checked : Object.keys(STATUS_LABEL_BY_VALUE);
  if (payload.archived === "show_archived") status.push("archived");

  const apiFilters = { status };
  if (payload.manager === "has_manager") apiFilters.has_manager = true;
  if (payload.manager === "no_manager") apiFilters.has_manager = false;
  if (payload.positions === "has_positions") apiFilters.has_positions = true;
  if (payload.positions === "no_positions") apiFilters.has_positions = false;
  return apiFilters;
}
